import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Copy, Edit2, Image as ImageIcon, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { API, handleImageUpload } from "./admin-utils";

const EMPTY_FORM = {
  slug: "",
  name: "",
  address: "",
  access_code: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  mode: "smart_fridge",
  logo_base64: null,
  is_active: true,
  smart_fridge_enabled: false,
  smart_fridge_url: "",
};

const generateAccessCode = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // omit ambiguous I, O, 0, 1
  let out = "";
  for (let i = 0; i < 6; i += 1) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
};

const slugify = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export function LocationsTab() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/locations`);
      setLocations(res.data);
    } catch {
      toast.error("Failed to load locations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, access_code: generateAccessCode() });
    setModalOpen(true);
  };

  const openEdit = (loc) => {
    setEditing(loc);
    setForm({
      slug: loc.slug,
      name: loc.name,
      address: loc.address || "",
      access_code: loc.access_code,
      contact_name: loc.contact_name || "",
      contact_email: loc.contact_email || "",
      contact_phone: loc.contact_phone || "",
      mode: loc.mode || "smart_fridge",
      logo_base64: loc.logo_base64,
      is_active: loc.is_active !== false,
      smart_fridge_enabled: loc.smart_fridge_enabled || false,
      smart_fridge_url: loc.smart_fridge_url || "",
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.slug || !form.access_code) {
      toast.error("Name, slug, and access code are required");
      return;
    }
    if (form.access_code.length !== 6) {
      toast.error("Access code must be exactly 6 characters");
      return;
    }
    if (form.smart_fridge_enabled) {
      const url = (form.smart_fridge_url || "").trim();
      if (!url) {
        toast.error("Please provide a Smart Fridge URL or disable the toggle");
        return;
      }
      if (!/^https?:\/\//i.test(url)) {
        toast.error("Smart Fridge URL must start with http:// or https://");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form, access_code: form.access_code.toUpperCase() };
      if (editing) {
        await axios.put(`${API}/admin/locations/${editing.id}`, payload);
        toast.success("Location updated");
      } else {
        await axios.post(`${API}/admin/locations`, payload);
        toast.success("Location created");
      }
      setModalOpen(false);
      fetchLocations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/locations/${deleteTarget.id}`);
      toast.success("Location deleted");
      setDeleteTarget(null);
      fetchLocations();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const copyUrl = (slug) => {
    const url = `${window.location.origin}/loc/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("URL copied to clipboard");
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#1aa59a]" />
      </div>
    );
  }

  return (
    <>
      <Button
        onClick={openCreate}
        className="bg-[#1aa59a] hover:bg-[#158a80] w-full mb-3"
        data-testid="add-location-button"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Location
      </Button>

      <div className="space-y-2">
        {locations.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            No locations yet. Add one to get started.
          </div>
        ) : (
          locations.map((loc) => (
            <Card key={loc.id} className={`overflow-hidden ${!loc.is_active ? "opacity-60" : ""}`}>
              <div className="flex items-center p-3 gap-3">
                {loc.logo_base64 ? (
                  <img src={loc.logo_base64} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-[#1aa59a] flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm truncate">{loc.name}</h3>
                  <p className="text-xs text-gray-500 truncate">
                    /loc/{loc.slug} · Code: <span className="font-mono">{loc.access_code}</span>
                    {!loc.is_active && " · INACTIVE"}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => copyUrl(loc.slug)} className="h-8 w-8 p-0" title="Copy URL">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(loc)} className="h-8 w-8 p-0">
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-500 h-8 w-8 p-0"
                    onClick={() => setDeleteTarget(loc)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Location" : "Add Location"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Display Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => {
                  const name = e.target.value;
                  setForm((f) => ({
                    ...f,
                    name,
                    // Auto-generate slug only on create
                    slug: editing ? f.slug : slugify(name),
                  }));
                }}
                placeholder="e.g. Acme Corp - London HQ"
              />
            </div>

            <div>
              <Label>URL Slug *</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                placeholder="acme-london"
              />
              {form.slug && (
                <p className="text-xs text-gray-500 mt-1">URL: /loc/{form.slug}</p>
              )}
            </div>

            <div>
              <Label>Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="1 Acme Street, London"
              />
            </div>

            <div>
              <Label>Access Code (6 chars) *</Label>
              <div className="flex gap-2">
                <Input
                  value={form.access_code}
                  onChange={(e) =>
                    setForm({ ...form, access_code: e.target.value.toUpperCase().slice(0, 6) })
                  }
                  className="font-mono uppercase"
                  maxLength={6}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForm({ ...form, access_code: generateAccessCode() })}
                >
                  Regen
                </Button>
              </div>
            </div>

            <div className="p-3 bg-gray-50 rounded-lg space-y-3">
              <Label className="text-xs uppercase tracking-wide text-gray-500">Customer Profile</Label>
              <p className="text-[11px] text-gray-500 -mt-2">
                Used to pre-fill the checkout form on Weekly Order sites. Also used for delivery contact on Smart Fridge sites.
              </p>

              <div>
                <Label className="text-xs">Contact Name</Label>
                <Input
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  placeholder="Jane Doe"
                  data-testid="loc-contact-name"
                />
              </div>

              <div>
                <Label className="text-xs">Contact Email</Label>
                <Input
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  placeholder="office@acme.com"
                  data-testid="loc-contact-email"
                />
              </div>

              <div>
                <Label className="text-xs">Contact Phone</Label>
                <Input
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  placeholder="07123 456789"
                  data-testid="loc-contact-phone"
                />
              </div>
            </div>

            <div className="p-3 bg-gray-50 rounded-lg space-y-2">
              <Label className="text-xs uppercase tracking-wide text-gray-500">Location Mode *</Label>
              <div className="flex gap-2">
                {[
                  { value: "smart_fridge", label: "Smart Fridge", desc: "Browse-only menu" },
                  { value: "weekly_order", label: "Weekly Order", desc: "Order with checkout" },
                ].map((opt) => {
                  const active = form.mode === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setForm({ ...form, mode: opt.value })}
                      className={`flex-1 rounded-lg border-2 p-3 text-left transition-all ${
                        active
                          ? "border-[#1aa59a] bg-white shadow-sm"
                          : "border-gray-200 bg-white hover:border-[#1aa59a]/50"
                      }`}
                      data-testid={`loc-mode-${opt.value}`}
                    >
                      <div className={`text-sm font-semibold ${active ? "text-[#1aa59a]" : "text-gray-700"}`}>
                        {opt.label}
                      </div>
                      <div className="text-[11px] text-gray-500 mt-0.5">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label>Logo (optional)</Label>
              <div
                className="mt-2 border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:border-[#1aa59a] transition-colors"
                onClick={() => document.getElementById("loc-logo-input").click()}
              >
                {form.logo_base64 ? (
                  <img src={form.logo_base64} alt="Preview" className="w-16 h-16 mx-auto rounded-lg object-cover" />
                ) : (
                  <div className="text-gray-400">
                    <ImageIcon className="w-6 h-6 mx-auto mb-1" />
                    <p className="text-xs">Upload logo</p>
                  </div>
                )}
              </div>
              <input
                id="loc-logo-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => handleImageUpload(e, (img) => setForm({ ...form, logo_base64: img }))}
              />
            </div>

            <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <Label className="cursor-pointer">Smart Fridge Site</Label>
                <Switch
                  checked={form.smart_fridge_enabled}
                  onCheckedChange={(checked) =>
                    setForm({ ...form, smart_fridge_enabled: checked })
                  }
                  data-testid="smart-fridge-toggle"
                />
              </div>
              <p className="text-xs text-gray-500">
                When enabled, customers see a fridge icon at the top of the location page linking
                to your custom layout image/page.
              </p>
              {form.smart_fridge_enabled && (
                <div className="pt-1">
                  <Label className="text-xs">Smart Fridge URL *</Label>
                  <Input
                    type="url"
                    value={form.smart_fridge_url}
                    onChange={(e) => setForm({ ...form, smart_fridge_url: e.target.value })}
                    placeholder="https://example.com/fridge-layout.jpg"
                    className="mt-1"
                    data-testid="smart-fridge-url-input"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">
                    Opens in a new tab. Hosted image link, Google Drive, or any URL works.
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <Label className="cursor-pointer">Active</Label>
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#1aa59a] hover:bg-[#158a80]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this location?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the location and all its promotions. Item availability for this location
              will be cleared from all menu items.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
